
export enum ValidationErrorType {
   empty = "empty",
   unexpectedValue = "unexpectedValue",
   invalidType = "invalidType",
   invalidEmail = "invalidEmail",
   invalidUrl = "invalidUrl",
   invalidDate = "invalidDate",
   invalidNumber = "invalidNumber",
   outOfBounds = "outOfBounds",
   duplicate = "duplicate",
   checksumFailedIBAN = "checksumFailedIBAN",
   invalidBIC = "invalidBIC",
   programNotFound = "programNotFound",
   domainLookupFailed = "domainLookupFailed",
   tooManySimultanous = "tooManySimultanous"
}

export type ValidationErrorMap<T extends {}> = Record<keyof T, ValidationErrorType | Record<string, any> | null | (ValidationErrorMap<any> | null)[]>;

export class ValidationResult<T extends {}> {
   readonly isValid: boolean;
   readonly errors: ValidationErrorMap<T>;

   constructor(errors: ValidationErrorMap<T>) {
      this.errors = errors;
      this.isValid = Object.values(errors).filter(e => e !== null).length == 0;
   }

   throwIfInvalid() {
      if (!this.isValid) {
         throw this;
      }
   }
}

export class Validation<T extends {}> {
   private readonly item: T;
   private readonly errors: ValidationErrorMap<T>;

   constructor(item: T) {
      this.item = item;
      const errors: any = {};

      Object.keys(item).forEach(key => {
         errors[key] = null;
      });

      this.errors = errors;
   }

   setDuplicate(key: keyof T): this {
      this.errors[key] = ValidationErrorType.duplicate;
      return this;
   }

   validateString(key: keyof T, maxlength: number | null, notEmpty: boolean = false): this {

      if (!this.errors[key]) {
         let value = this.item[key];

         if (typeof value != "string") {
            this.errors[key] = ValidationErrorType.invalidType;
         } else if (maxlength && value.length > maxlength) {
            this.errors[key] = ValidationErrorType.outOfBounds;
         } else if (notEmpty && value.trim().length <= 0) {
            this.errors[key] = ValidationErrorType.empty;
         }
      }

      return this;
   }

   validateNonEmptyString(key: keyof T, maxlength: number | null): this {

      if (!this.errors[key]) {
         let value = this.item[key];

         if (typeof value != "string") {
            this.errors[key] = ValidationErrorType.unexpectedValue;
         } else {
            let trimmedValue = value.trim();
            this.item[key] = trimmedValue as any;

            if (trimmedValue.length == 0) {
               this.errors[key] = ValidationErrorType.empty;
            } else if (maxlength && trimmedValue.length > maxlength) {
               this.errors[key] = ValidationErrorType.outOfBounds;
            }
         }
      }

      return this;
   }

   validateBoolean(key: keyof T, allowNull: boolean = false, mustBeTrue: boolean = false): this {
      if (!this.errors[key]) {
         let value = this.item[key];

         if(value != null) {
            if (typeof value != "boolean") {
               this.errors[key] = ValidationErrorType.invalidType;
            } else if (mustBeTrue) {
               if(!value) this.errors[key] = ValidationErrorType.unexpectedValue;
            }
         } else if (!allowNull) {
            this.errors[key] = ValidationErrorType.empty;
         }
      }

      return this;
   }

   validateEmail(key: keyof T, allowEmpty: boolean = false): this {
      this.validateString(key, null);

      let emailRegex = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;

      if (!this.errors[key]) {
         let value = this.item[key] as any;

         if (typeof (value) !== 'string' || !emailRegex.test(value.toLowerCase())) {
            if (!allowEmpty) {
               this.errors[key] = ValidationErrorType.invalidEmail;
            } else {
               if (typeof (value) !== 'string' || value !== "") {
                  this.errors[key] = ValidationErrorType.invalidEmail;
               }
            }
         }
      }

      return this;
   }

   /* validateUrl(key: keyof T, maxlength: number | null = null, allowEmpty: boolean = false): this {
      this.validateString(key, null);

      //todo url regex
      let urlRegex = /^$/;

      if (!this.errors[key]) {
         let value = this.item[key] as any;

         if (typeof (value) !== 'string' || !urlRegex.test(value.toLowerCase())) {
            if (!allowEmpty) {
               this.errors[key] = ValidationErrorType.invalidUrl;
            } else {
               if (typeof (value) !== 'string' || value !== "") {
                  this.errors[key] = ValidationErrorType.invalidType;
               }
            }
         } else if (maxlength && value.length > maxlength) {
            this.errors[key] = ValidationErrorType.outOfBounds;
         }
      }

      return this;
   } */

   validateUsername(key: keyof T, maxlength: number | null, allowNull: boolean = false): this {
      let regex = /^[\w@.-]{2,}$/;

      if (!this.errors[key]) {
         let value = this.item[key];

         if(value != null) {
            if (typeof value != "string" || !regex.test(value)) {
               this.errors[key] = ValidationErrorType.invalidType;
            } else if (maxlength && value.length > maxlength) {
               this.errors[key] = ValidationErrorType.outOfBounds;
            }
         } else if (!allowNull) {
            this.errors[key] = ValidationErrorType.empty;
         }
      }

      return this;
   }

   validateDate(key: keyof T, allowNull: boolean = false): this {
      let value = this.item[key] as any;
      if (value != null) {
         this.validateString(key, null);

         if (!this.errors[key]) {
            if (isNaN(Date.parse(value))) {
               this.errors[key] = ValidationErrorType.invalidDate;
            }
         }
      } else if (!allowNull) {
         this.errors[key] = ValidationErrorType.empty;
      }

      return this;
   }

   validatePastDate(key: keyof T, allowNull: boolean = false): this {
      let value = this.item[key] as any;
      if (value != null) {
         this.validateString(key, null);

         if (!this.errors[key]) {
            let value = this.item[key] as any;
            let time = Date.parse(value);
            if (isNaN(time) || time >= new Date().valueOf()) {
               this.errors[key] = ValidationErrorType.invalidDate;
            }
         }
      } else if (!allowNull) {
         this.errors[key] = ValidationErrorType.empty;
      }

      return this;
   }

   validateTime(key: keyof T): this {
      this.validateString(key, null);

      if (!this.errors[key]) {
         let value = this.item[key] as any;

         let timeRegex = /^(2[0-3]|[01][0-9]):([0-5][0-9])$/;

         if (!timeRegex.test(value)) {
            this.errors[key] = ValidationErrorType.invalidDate;
         }
      }

      return this;
   }

   validateTimeLaterTime(key: keyof T, afterKey: keyof T): this {
      this.validateTime(key);

      if (!this.errors[key]) {
         let value = this.item[key] as any;
         let afterValue = this.item[afterKey] as any;

         if (value <= afterValue) {
            this.errors[key] = ValidationErrorType.invalidDate;
         }
      }

      return this;
   }

   validateFutureDate(key: keyof T, daysAhead: number = 0): this {
      this.validateString(key, null);

      if (!this.errors[key]) {
         let value = this.item[key] as any;
         let time = Date.parse(value);
         let today = new Date();
         today.setDate(today.getDate() + daysAhead);
         if (isNaN(time) || time <= today.valueOf()) {
            this.errors[key] = ValidationErrorType.invalidDate;
         }
      }

      return this;
   }

   validateDateSameDayLaterTime(key: keyof T, afterKey: keyof T): this {
      this.validateString(key, null);

      if (!this.errors[key]) {
         let value = this.item[key] as any;
         let time = Date.parse(value);
         if (isNaN(time)) {
            this.errors[key] = ValidationErrorType.invalidDate;
         } else if (!this.errors[afterKey]) {
            let timeEarlier = Date.parse(this.item[afterKey] as any);
            if (timeEarlier > time) {
               this.errors[key] = ValidationErrorType.invalidDate;
            } else {
               let dateEarlier = new Date(timeEarlier);
               let date = new Date(time);
               if (date.getFullYear() != dateEarlier.getFullYear() || date.getMonth() != dateEarlier.getMonth() || date.getDate() != dateEarlier.getDate()) {
                  this.errors[key] = ValidationErrorType.invalidDate;
               }
            }
         }
      }

      return this;
   }


   validateDateLater(key: keyof T, afterKey: keyof T): this {
      this.validateString(key, null);

      if (!this.errors[key]) {
         let value = this.item[key] as any;
         let time = Date.parse(value);
         if (isNaN(time)) {
            this.errors[key] = ValidationErrorType.invalidDate;
         } else if (!this.errors[afterKey]) {
            let timeEarlier = Date.parse(this.item[afterKey] as any);
            if (timeEarlier > time) {
               this.errors[key] = ValidationErrorType.invalidDate;
            }
         }
      }

      return this;
   }

   validateEquals(key: keyof T, expected: any): this {
      if (!this.errors[key]) {
         let value = this.item[key];

         if (value !== expected) {
            this.errors[key] = ValidationErrorType.unexpectedValue;
         }
      }

      return this;
   }

   validateUsingFunction(key: keyof T, validationFunction: (any: any) => ValidationResult<any> | ValidationResult<any>[], allowNull: boolean = false): this {
      if (!this.errors[key]) {
         let value = this.item[key];

         if (value != null) {
            let validationResult = validationFunction(value);

            if (Array.isArray(validationResult)) {
               if (validationResult.filter(r => !r.isValid).length > 0) {
                  this.errors[key] = validationResult.map(r => r.errors);
               }
            }
            else if (!validationResult.isValid) {
               this.errors[key] = validationResult.errors;
            }
         } else if (!allowNull) {
            this.errors[key] = ValidationErrorType.empty;
         }

      }

      return this;
   }

   validateAllUsingFunction(key: keyof T, validationFunction: (any: any) => ValidationResult<any>, minElementCount: number = 0, allowNull: boolean = false, maxElementCount: number = Number.MAX_SAFE_INTEGER): this {
      if (!this.errors[key]) {
         let value = this.item[key] as any as any[];

         if (value != null) {
            let validationResults = value.map(i => validationFunction(i));

            if (validationResults.filter(r => !r.isValid).length > 0) {
               this.errors[key] = validationResults.map(r => r.isValid ? null : r.errors);
            } else if (value.length < minElementCount) {
               this.errors[key] = ValidationErrorType.outOfBounds;
            } else if (value.length > maxElementCount) {
               this.errors[key] = ValidationErrorType.outOfBounds;
            }
         } else if (!allowNull) {
            this.errors[key] = ValidationErrorType.empty;
         }
      }

      return this;
   }

   validateInteger(key: keyof T, min: number | null = null, max: number | null = null, allowNull: boolean = false): this {
      if (!this.errors[key]) {
         let value = this.item[key];

         if (value == null && allowNull)
            return this;
         else
            if (typeof value != "number" || !Number.isInteger(value)) {
               this.errors[key] = ValidationErrorType.unexpectedValue;
            } else if ((min !== null && value < min) || (max !== null && value > max)) {
               this.errors[key] = ValidationErrorType.outOfBounds;
            }
      }
      return this;
   }

   validateNumber(key: keyof T, min: number | null = null, max: number | null = null, allowNull: boolean = false): this {
      if (!this.errors[key]) {
         let value = this.item[key];

         if (value == null && allowNull)
            return this;
         else
            if (typeof value != "number") {
               this.errors[key] = ValidationErrorType.unexpectedValue;
            } else if ((min !== null && value < min) || (max !== null && value > max)) {
               this.errors[key] = ValidationErrorType.outOfBounds;
            }
      }
      return this;
   }

   validateIBAN(key: keyof T): this {
      this.validateString(key, null);

      if (!this.errors[key]) {
         let value = this.item[key] as any as string;

         var isValid = isValidIBANNumber(value);

         if (isValid === false) {
            this.errors[key] = ValidationErrorType.outOfBounds;
         } else if (isValid !== 1) {
            this.errors[key] = ValidationErrorType.checksumFailedIBAN;
         }
      }

      return this;
   }

   validateBIC(key: keyof T): this {
      this.validateString(key, null);

      if (!this.errors[key]) {
         let value = this.item[key] as any as string;

         if (!/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value)) {
            this.errors[key] = ValidationErrorType.invalidBIC;
         }
      }

      return this;
   }

   set(key: keyof T, value: ValidationErrorType | ValidationErrorMap<any> | ValidationErrorMap<any>[]): this {
      this.errors[key] = value;
      return this;
   }

   finish(): ValidationResult<T> {
      return new ValidationResult(this.errors);
   }
}

/*
 * Validate a IBAN. Source: https://stackoverflow.com/a/35599724
 *
 * Returns 1 if the IBAN is valid 
 * Returns FALSE if the IBAN's length is not as should be (for CY the IBAN Should be 28 chars long starting with CY )
 * Returns any other number (checksum) when the IBAN is invalid (check digits do not match)
 */
function isValidIBANNumber(input: string): false | number {

   function mod97(string: string) {
      var checksum = parseInt(string.slice(0, 2)), fragment;
      for (var offset = 2; offset < string.length; offset += 7) {
         fragment = String(checksum) + string.substring(offset, offset + 7);
         checksum = parseInt(fragment, 10) % 97;
      }
      return checksum;
   }

   var CODE_LENGTHS = {
      AD: 24, AE: 23, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
      CH: 21, CR: 21, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, ES: 24,
      FI: 18, FO: 18, FR: 27, GB: 22, GI: 23, GL: 18, GR: 27, GT: 28, HR: 21,
      HU: 28, IE: 22, IL: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28,
      LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27,
      MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29,
      RO: 24, RS: 22, SA: 24, SE: 24, SI: 19, SK: 24, SM: 27, TN: 24, TR: 26
   } as { [index: string]: number; };

   var iban = String(input).toUpperCase().replace(/[^A-Z0-9]/g, ''), // keep only alphanumeric characters
      code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/), // match and capture (1) the country code, (2) the check digits, and (3) the rest
      digits;
   // check syntax and length
   if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
      return false;
   }
   // rearrange country code and check digits, and convert chars to ints
   digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, function (letter: string): string {
      return (letter.charCodeAt(0) - 55).toString();
   });
   // final check
   return mod97(digits);
}